import React from 'react'
import { Alert, Checkbox, Flex, Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import type { ToolSelection } from '../types'
import { ActionPolicy, actionPolicy, policyReady } from '../action-policy'
import { inputSchema } from '../tool-configuration'

type ActionPolicyEditorProps = {
    tool: ToolSelection
    onChange: (tool: ToolSelection) => void
    showApproval?: boolean
}

export function ActionPolicyEditor({ tool, onChange, showApproval = true }: ActionPolicyEditorProps) {
    const { t } = useTranslation()
    const policy = actionPolicy(tool)
    const schema = inputSchema(tool)
    const fixed = JSON.parse(tool.fixedArgumentsJson) as Record<string, unknown>
    const ready = policyReady(tool)
    const outputPaths = tool.allowedOutputPaths ?? []

    const update = (patch: Partial<ActionPolicy>) =>
        onChange({
            ...tool,
            policyJson: JSON.stringify({ ...policy, approved: false, ...patch }),
        })

    const sharedOption = {
        value: 'shared',
        label: t('Shared campaign information'),
    }

    const newRecordOption = {
        value: 'new_record',
        label: t('Create a new record'),
    }

    const personalOption = {
        value: 'personal',
        label: t('Existing personal records — unavailable'),
    }

    // A read-only action cannot create a record.
    const audienceOptions = tool.readOnly
        ? [sharedOption, personalOption]
        : [sharedOption, newRecordOption, personalOption]

    const inputOptions = (tool.allowedDynamicArguments ?? []).map((field) => ({
        value: field,
        label: schema.properties?.[field]?.title ?? field,
        disabled: Object.prototype.hasOwnProperty.call(fixed, field),
    }))

    const outputOptions = outputPaths.map((path) => ({
        value: path,
        label: path.replace(/^\/data\//, '').replace(/\//g, ' › '),
    }))

    return (
        <Flex
            vertical
            gap="small"
        >
            <Typography.Text strong>{t('Access settings')}</Typography.Text>
            <Typography.Text type="secondary">
                {t(
                    'Resource settings stay fixed. Only the inputs and output fields below are available to the conversation agent.'
                )}
            </Typography.Text>
            <Select
                aria-label={t('Data access')}
                value={policy.audience}
                onChange={(audience) => update({ audience })}
                options={audienceOptions}
            />
            {policy.audience === 'personal' && (
                <Alert
                    type="warning"
                    message={t(
                        'Existing personal records need a trusted association. This action cannot be enabled yet.'
                    )}
                />
            )}
            <Typography.Text>{t('Conversation inputs')}</Typography.Text>
            <Select
                mode="multiple"
                aria-label={t('Conversation inputs')}
                value={policy.dynamic_arguments}
                onChange={(dynamic_arguments) => update({ dynamic_arguments })}
                options={inputOptions}
            />
            <Typography.Text>{t('Information the agent may receive')}</Typography.Text>
            <Select
                mode="multiple"
                aria-label={t('Information the agent may receive')}
                value={policy.output_paths}
                onChange={(output_paths) => update({ output_paths })}
                options={outputOptions}
                placeholder={t('Only success or failure')}
            />
            {outputPaths.length === 0 && (
                <Typography.Text type="secondary">
                    {t('This action has no declared output fields. Its returned data stays hidden.')}
                </Typography.Text>
            )}
            {!ready && policy.audience !== 'personal' && (
                <Alert
                    type="warning"
                    message={t('Fix required resource settings before approving this action.')}
                />
            )}
            {showApproval && (
                <Checkbox
                    checked={policy.approved}
                    disabled={!ready}
                    onChange={(event) => update({ approved: event.target.checked })}
                >
                    {t('I approve this scope and confirm that the selected output is suitable for every participant.')}
                </Checkbox>
            )}
        </Flex>
    )
}
